import { useMutation } from '@apollo/client';
import { GetStaticPaths, GetStaticProps } from 'next';
import dynamic from "next/dynamic";
import { useRouter } from 'next/router';
import { FC, useEffect, useMemo } from 'react';
import LazyHydrate from 'react-lazy-hydration';
import { wcApiDataService } from 'functions/wcApi';
import {
  ColorTaxonomyType,
  CompositeComponentType,
  ProductDetailType,
  SeoMetaTags,
  GoogleReviewModel,
} from 'constants/dataTypes';
import { CREATE_CART } from 'gqlStrings/mutations/addToCart';
import { getCategoryBasedSectionList } from 'functions/notion';
import { useGlassesStore } from 'zus/glassesStore';
import { ProductDetailTabsComponent } from 'components/ProductDetail/ProductDetailTabsComponent';
import ShapeSwitcher from 'components/ProductDetail/ShapeSwitcher';
import ProductDetailBreadcrumb from 'components/ProductDetail/ProductDetailBreadcrumb';
import ProductImageSlider from 'components/ProductImageSlider/ProductImageSlider';
import { useVTOStore } from 'zus/vtoStore';
import FrameAddToCartComponent from 'components/ProductDetail/FrameAddToCartComponent';
import FrameProductHeaderComponent from 'components/ProductDetail/FrameProductHeaderComponent';
import { ProductQuerySelector } from 'components/ProductDetail/ProductQuerySelector';
import * as ga from '../../../../lib/ga';
import PriceBadge from 'components/Others/PriceBadge';
import WarningMessageComponent from 'components/ProductDetail/WarningMessageComponent';
import useDeviceType from 'hooks/useDeviceType';
import { getSession } from 'functions/session';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import { getSeoMetaTags, getGoogleReviews, getFrameArmsOptions } from 'utils/common.utils';
import instagramService, { InstagramVideo } from 'services/instagram.service';
import { ProductVTOSelector } from 'components/ProductDetail/ProductVTOSelector';
import { useProductStore } from 'zus/productStore';
import AfterPayInfo from 'components/Others/AfterPayInfo';
import ZipPayInfo from 'components/Others/ZipPayInfo';
import cx from 'classnames';
import checkFrameArmsVariation from 'functions/checkFrameArmsVariation';
import { ProductOptionsWithHex } from 'constants/dataDresdenTypes';
import ColorSizeFrameComponent from 'components/ProductDetail/ColorSizeFrameComponent';
import ColorSizeArmsComponent from 'components/ProductDetail/ColorSizeArmsComponent';
import { ProductTitle } from 'components/ProductDetail/ProductTitle';
import { PrideDiscountBanner } from 'components/ProductDetail/PrideDiscountBanner';

const PDModal = dynamic(() => import('components/PD/PDModal'), { ssr: false });
const CommunityShowcase = dynamic(() => import('components/ProductDetail/CommunityShowcase'), { ssr: false });
const CartRecommendedProductsLazyLoaded = dynamic(() => import('components/ShoppingCart/CartRecommendedProductsLazyLoad'), { ssr: false });
const GooglePageReviews = dynamic(() => import('components/GoogleReview/GooglePageReviews'), { ssr: false });

const ProductPage: FC<{
  glassesProductData: CompositeComponentType;
  colors: ColorTaxonomyType[];
  productDetailMap: ProductDetailType[];
  framesCategory: any;
  seoMetaTags: SeoMetaTags;
  reviews: GoogleReviewModel;
  frameArmsOptions: ProductOptionsWithHex;
  instagramVideos: InstagramVideo[];
}> = ({
  glassesProductData,
  colors,
  productDetailMap,
  framesCategory,
  seoMetaTags,
  reviews,
  frameArmsOptions,
  instagramVideos
}) => {
    const router = useRouter();
    const setColors = useVTOStore((state) => state.setColors);
    const setFrameShape = useGlassesStore((state) => state.setFrameShape);
    const isActiveLensSelectionPanel = useProductStore((state: any) => state.isActiveLensSelectionPanel);

    const regularPrice: number = framesCategory.meta?.category_price_reference.regular_price;
    const salesPrice: number = framesCategory.meta?.category_price_reference.price;

    useMemo(() => {
      setColors(router.locale, colors);
    }, [colors, router.locale, setColors]);

    useEffect(() => {
      const { shape } = router.query;
      if (shape === 'round') {
        setFrameShape('round');
      } else {
        setFrameShape('classic');
      }
    }, [router.query, setFrameShape]);

    const [mutateCreateCart] = useMutation(CREATE_CART, {
      context: { clientName: 'laravel' }
    });

    useEffect(() => {
      if (process.browser && !getSession()) {
        mutateCreateCart({
          variables: {
            input: {
              isEmpty: true
            }
          }
        });
      }
    }, [mutateCreateCart]);

    useEffect(() => {
      ga.addDataLayer({
        event: 'ImpCompleted',
        ecommerce: {
          detail: {
            actionField: { list: 'Shop' },
            products: [
              {
                name: 'Frames - ' + router.query.product,
                price: regularPrice,
                category: 'Frames',
                variant: router.query.product
              }
            ]
          }
        }
      });
    }, [router, regularPrice]);

    const deviceType = useDeviceType();

    const checkVariationOnColorPicker = (type: string, size: string, color: string) => {
      const shape = router.query.shape as string || 'classic';
      if (!frameArmsOptions) return false;
      return checkFrameArmsVariation(shape, frameArmsOptions, type, size, color);
    };

    if (router.isFallback) {
      return (
        <div className="flex h-[70vh] items-center justify-center">
          Loading...
        </div>
      );
    }

    return (
      <div className="relative w-full">
        <FrameProductHeaderComponent price={regularPrice} reviews={reviews} />
        <div className={cx("mx-auto px-4 pt-6 relative", {
          "max-w-[1224px]": !isActiveLensSelectionPanel
        })}>
          <ProductDetailBreadcrumb categorySlug="frames" />
          <div className="grid lg:grid-cols-[650px,auto] gap-x-8 grid-cols-1 mt-[24px] gap-4">
            <div className="lg:flex lg:w-full lg:flex-col">
              <ProductTitle
                categorySlug={'frames'}
                showOnMobile={true}
                price={salesPrice}
              />
              <ProductImageSlider
                category="frames"
                frameColor={router.query.product as string}
                instagramVideos={instagramVideos}
              />
            </div>
            <div className="">
              <ProductTitle
                categorySlug={'frames'}
                price={salesPrice}
              />
              {!isActiveLensSelectionPanel && (
                <>
                  <ShapeSwitcher frameArmsOptions={frameArmsOptions} />
                  <ColorSizeFrameComponent
                    categorySlug={'frames'}
                    frameArmsOptions={frameArmsOptions}
                    frameColor={router.query.product as string}
                    checkVariationOnColorPicker={checkVariationOnColorPicker}
                  />
                  <ColorSizeArmsComponent
                    frameArmsOptions={frameArmsOptions}
                    frameColor={router.query.product as string}
                    checkVariationOnColorPicker={checkVariationOnColorPicker}
                  />
                </>
              )}
              <WarningMessageComponent />
              <FrameAddToCartComponent
                checkVariationOnColorPicker={checkVariationOnColorPicker}
                frameColor={router.query.product as string}
                price={salesPrice} />
            </div>
          </div>
          {!isActiveLensSelectionPanel && <ProductDetailTabsComponent categorySlug={'frames'} />}
          {!isActiveLensSelectionPanel && <CartRecommendedProductsLazyLoaded frameColorZ={router.query.product as string} shoppingCart={false} />}
          <LazyHydrate whenVisible>
            <CommunityShowcase />
          </LazyHydrate>
          <GooglePageReviews reviews={reviews} />
        </div>
        <LazyHydrate whenIdle>
          <PDModal />
        </LazyHydrate>
        <GlobalSeoMetaTags seoMetaTags={seoMetaTags} />
      </div>
    );
  }

export default ProductPage;

export const getStaticPaths: GetStaticPaths = async () => {
  var paths: any = [];
  if (process.env.NEXT_PUBLIC_ENV !== 'production') {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  var glassesProducts: any = await wcApiDataService('paths/shop', 'hasSubCategory&exclude_cat=spare-parts,frames,accessories,contact-lenses', '');
  if (!glassesProducts) {
    return {
      paths,
      fallback: 'blocking'
    };
  }

  for (const [country, data] of Object.entries(glassesProducts) as any) {
    for (const [category_slug, color_list] of Object.entries(data.items) as any) {
      color_list.map((color_id: any) => {
        paths.push({
          params: { category: category_slug, product: data.colors[color_id] },
          locale: country
        });
      })
    }
  }


  return {
    paths,
    fallback: 'blocking'
  };
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const paramCat: any = 'frames';

  var productDetailPageData: any = await wcApiDataService('glasses/frame-set', '', locale);
  var framesCategory: any = await wcApiDataService('cat/frames', '', locale);

  const glassesProductData = productDetailPageData?.components;
  const colors = productDetailPageData?.colors;

  if (!glassesProductData) {
    return {
      redirect: {
        destination: '/shop/frames',
        statusCode: 301
      }
    }
  }
  const frameProduct = glassesProductData.Frame.product.variations.find?.((item: any) => item.meta.attribute_pa_color == params?.product);
  const armsProduct = glassesProductData.Arms.product.variations.find?.((item: any) => item.meta.attribute_pa_color == params?.product);

  // If product colour not found, return 404
  if (!frameProduct || !armsProduct) {
    return {
      redirect: {
        destination: '/shop/frames',
        statusCode: 301
      }
    }
  }

  const productDetailList = await getCategoryBasedSectionList(paramCat, locale!);

  const seoMetaTags = await getSeoMetaTags('/shop/frames/' + params?.product, locale);

  const frameArmsOptions = await getFrameArmsOptions(locale);
  const reviews = await getGoogleReviews(locale);
  const instagramVideos = await instagramService.getInstagramVideos();

  return {
    props: {
      glassesProductData,
      colors,
      productDetailMap: productDetailList,
      framesCategory,
      seoMetaTags,
      reviews,
      frameArmsOptions,
      instagramVideos,
    }
  };
};
